<script>
  import Icon from '#lib/Icon.svelte';
  import { unique_classes } from '#lib/util.js';
  import { IMAGE_CONFIG } from '#lib/image_config.js';
  import t from '#lib/i18n.svelte.js';
  import PlayPauseButton from '#lib/PlayPauseButton.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import { dev } from '$app/env';

  let {
    media_type = 'image', 
    /** @type {string} */
    class: css_class,
    /** @type {integer} natural width of the image in pixels */
    width,
    /** @type {integer} natural height of the image in pixels */
    height,
    /** @type {integer} display width in pixels - if not set, will use natural width */
    display_width,
    /** @type {integer} display height in pixels - if not set, will use natural height */
    display_height,
    /** @type {float} number between 0 and 1 */
    focal_point_x,
    /** @type {float} number between 0 and 1 */
    focal_point_y,
    /** @type {boolean} fit within the boundaries of the image container */
    fit_to_frame,
    /** @type {boolean} duration of video in seconds */
    duration,
    /** @type {boolean} whether the video has an audio track */
    audio_track,
    /** @type {string} */
    src,
    /** @type {string} */
    alt,
    /** @type {string} */
    aspect_ratio = undefined,
    /** @type {Array<{variant_id: string, width: number, height: number, format: string}>} */
    variants = [],
    /** @type {boolean} */
    is_editing,
    /** @type {string} primary label: displayed if no src if provided */
    placeholder = t('add_an_image'),
    /** @type {string} secondary label: displayed if no src if provided */
    hint = '',
    /** 
     * @type {string} CSS sizes attribute for responsive images
     * Note: We specify the CSS display size rather than the actual pixel size needed.
     * The browser automatically:
     * 1. Reads the CSS size (e.g. 100px)
     * 2. Multiplies by the device pixel ratio (e.g. 2x for retina)
     * 3. Selects an appropriate image from srcset
     */
    sizes = display_width ? `${display_width}px` : '100vw',
    autoplay = true,
    /** @type {boolean} show play & pause button */
    show_playback_control = false,
    /** @type {'lazy' | 'eager' | 'auto'} */  
    loading = 'lazy',
    style = '',
    // NOTE: Currently no other way to pass in data-field-name as a separate prop
    field_name,
    checkerboard = false,
    media_ref = $bindable(),
    timestamp = 0,
    // disable auto play in dev mode
    is_playing = autoplay && !dev,
    /** @type {boolean} enable image zoom on click */
    zoom
  } = $props();

  let mute_video = $state(true);

  let class_names = $derived(unique_classes(
    css_class, 
    'media-item',
    media_type, // 'image' or 'video' - used by CSS selectors instead of element type checks
    fit_to_frame && 'fit-to-frame',
    checkerboard && 'checkerboard',
    src?.split('.').pop().toLowerCase()
  ));

  
  $effect(()=>{
    if(is_editing && media_ref) {
      media_ref.pause();
      is_playing = false;
    }
  });

  function update_timestamp(){
    timestamp = media_ref?.currentTime;
    is_playing = !media_ref?.paused;
  }

  function toggle_play(e) {
    e.preventDefault();
    if(!!media_ref.currentTime > 0 && !media_ref.paused && !media_ref.ended && media_ref.readyState > 2){
      media_ref.pause();
      is_playing = false;
    } else {
      media_ref.play();
      is_playing = true;
    }
  }

  function build_variant_src(variant_id) {
    return `/assets/${variant_id}`;
  }

  function build_local_srcset(variant_list) {
    if (!Array.isArray(variant_list) || variant_list.length === 0) return '';
    return variant_list
      .sort((a, b) => a.width - b.width)
      .map(variant => `${build_variant_src(variant.variant_id)} ${variant.width}w`)
      .join(', ');
  }

  const local_variants = $derived(
    Array.isArray(variants) 
      ? variants.filter(v => v?.variant_id && v?.width && v?.format)
      : []
  );

  const local_webp_variants = $derived(local_variants.filter(variant => variant.format === 'webp'));
  const local_avif_variants = $derived(local_variants.filter(variant => variant.format === 'avif'));

  const local_webp_srcset = $derived(build_local_srcset(local_webp_variants));
  const local_avif_srcset = $derived(build_local_srcset(local_avif_variants));

  /**
   * Parse sizes attribute to estimate the display width in pixels.
   * Returns null if unable to parse (e.g., viewport-relative units).
   * @param {string} sizes_value
   * @returns {number | null}
   */
  function parse_sizes_px(sizes_value) {
    if (!sizes_value) return null;
    const match = sizes_value.match(/^(\d+)px$/);
    return match ? parseInt(match[1], 10) : null;
  }

  // Include AVIF only when the smallest variant is reasonable for the display size
  // (covers up to 3x DPR with margin). For small thumbnails, WebP is sufficient.
  const AVIF_SIZE_MULTIPLIER = 4;
  const should_include_avif = $derived.by(() => {
    if (!local_avif_srcset) return false;
    const estimated_px = parse_sizes_px(sizes);
    if (estimated_px === null) return true; // Can't determine, include AVIF
    const smallest_avif = Math.min(...local_avif_variants.map(v => v.width));
    return smallest_avif <= estimated_px * AVIF_SIZE_MULTIPLIER;
  });

  // Variants generate progressively after upload — an incomplete set must not
  // win over the original, or the browser upscales a small early variant
  // (blurry freshly-pasted images). The set counts as complete once the
  // largest content WebP width the config expects for this image exists.
  const expected_webp_width = $derived.by(() => {
    const image_width = Number(width) || null;
    if (!image_width) return 0; // unknown width: any srcset counts
    const fitting = IMAGE_CONFIG.content_webp_variants
      .map(variant => variant.width)
      .filter(variant_width => variant_width <= image_width);
    return fitting.length ? Math.max(...fitting) : Math.round(image_width);
  });
  const variants_complete = $derived(local_webp_variants.some(v => v.width >= expected_webp_width));

  // Only include srcset if we're not using an SVG (which doesn't need responsive sizing)
  let should_use_local_srcset = $derived(variants_complete && local_webp_srcset && !/\.svg$/i.test(src));
  let is_svg = $derived(/\.svg$/i.test(src));
  let computed_loading = $derived(is_svg ? 'eager' : loading);
  let computed_fetchpriority = $derived((is_svg || computed_loading === 'eager') ? 'high' : undefined);

  /**
   * Parse aspect_ratio prop (number, decimal string, or fraction string like "3 / 4")
   * into a float. Returns undefined when unparseable.
   */
  function parse_aspect_ratio(val) {
    if (typeof val === 'number' && val > 0) return val;
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed.includes('/')) {
        const [n, d] = trimmed.split('/').map(s => parseFloat(s.trim()));
        if (d > 0) return n / d;
      }
      const parsed = parseFloat(trimmed);
      if (parsed > 0) return parsed;
    }
    return undefined;
  }

  /**
   * Safari breaks object-fit: cover/contain on <video> elements.
   * Workaround: wrap video in a div that handles aspect-ratio + overflow clipping,
   * and size the video to fill one axis while overflowing the other (simulating cover).
   *
   * Returns 'height' when the video should fill the wrapper's height (width overflows),
   * 'width' when it should fill the wrapper's width (height overflows),
   * or 'contain' when the video should fit inside the wrapper.
   */
  let video_cover_axis = $derived.by(() => {
    if (fit_to_frame || !width || !height) return 'contain';
    const video_ratio = width / height;
    const target = parse_aspect_ratio(aspect_ratio) ?? video_ratio;
    return video_ratio >= target ? 'height' : 'width';
  });
</script>


{#if src}
  {#if media_type === 'image'}
    {#if should_use_local_srcset}
      <picture class={class_names}>
        {#if should_include_avif}
          <source
            type="image/avif"
            srcset={local_avif_srcset}
            sizes={sizes}
          />
        {/if}
        <source
          type="image/webp"
          srcset={local_webp_srcset}
          sizes={sizes}
        />
        <img
          width={display_width ?? width}
          height={display_height ?? height}
          style:width={display_width ? display_width + 'px' : undefined}
          style:height={display_height ? display_height + 'px' : undefined}
          {style}
          style:aspect-ratio={
            // We only apply aspect ratio if we don't have both display width and height set. They should override aspect ratio.
            !display_width && !display_height ? aspect_ratio : undefined
          }
          style:object-position={ focal_point_x != null && focal_point_y != null && `${focal_point_x * 100}% ${focal_point_y * 100}%` }
          src={src}
          srcset={local_webp_srcset}
          sizes={sizes}
          alt={alt}
          class={class_names}
          loading={computed_loading}
          {...(computed_fetchpriority ? { fetchpriority: computed_fetchpriority } : {})}
          decoding={'async'}
          data-field-name={field_name}
        />
      </picture>
    {:else}
      <img
        width={display_width ?? width}
        height={display_height ?? height}
        style:width={display_width ? display_width + 'px' : undefined}
        style:height={display_height ? display_height + 'px' : undefined}
        {style}
        style:aspect-ratio={
          // We only apply aspect ratio if we don't have both display width and height set. They should override aspect ratio.
          !display_width && !display_height ? aspect_ratio : undefined
        }
        style:object-position={ focal_point_x != null && focal_point_y != null && `${focal_point_x * 100}% ${focal_point_y * 100}%` }
        src={src}
        alt={alt}
        class={class_names}
        loading={computed_loading}
        {...(computed_fetchpriority ? { fetchpriority: computed_fetchpriority } : {})}
        decoding={'async'}
        data-field-name={field_name}
      />
    {/if}
  {:else if media_type ==='video'}
    {#if show_playback_control}
      <div class='media-item-controls flex-column gap-y g-2'>
          <PlayPauseButton 
            class='media-item-playback'
            {is_playing}
            {toggle_play}
            bind:timestamp
            {duration}
          title={alt}
        />
        {#if audio_track}
          <button class='media-item-mute' onclick={(e) => {
            e.preventDefault(); 
            mute_video = !mute_video;
          }}>
            <ButtonLabel
              start_icon={mute_video ? 'speaker-mute' : 'speaker-high'}
              variant='brand filled'
            />
          </button>
        {/if}
      </div>
    {/if}
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <!-- svelte-ignore a11y_media_has_caption -->
    <div
      class="video-wrapper"
      class:cover-by-height={video_cover_axis === 'height'}
      class:cover-by-width={video_cover_axis === 'width'}
      class:fit-to-frame={fit_to_frame}
      {style}
      style:width={display_width ? display_width + 'px' : undefined}
      style:height={display_height ? display_height + 'px' : undefined}
      style:aspect-ratio={aspect_ratio || (width && height ? `${width} / ${height}` : undefined)}
      data-field-name={field_name}
    >
      <video
        loading='lazy'
        preload='metadata'
        bind:this={media_ref}
        playsinline={true}
        muted={mute_video}
        {...(true || autoplay && !dev ? {
          autoplay: 'autoplay'
        } : {})}
        ontimeupdate={update_timestamp}
        loop={true}
        width={width}
        height={height}
        src={src}
        alt={alt}
        class={class_names}
        {...(zoom ? {
          onclick: toggle_play
        } : {})}
      ></video>
    </div>
  {:else}
    media type {media_type} not supported.
  {/if}
{:else}  
  <div
    style:width={ display_width ? display_width + 'px' : width + 'px'}
    style:height={display_height ? display_height + 'px' : height + 'px'}
    class='image placeholder-image {class_names} text-center'
    style:aspect-ratio={aspect_ratio ?? ''}    
    {style}
  >
    <Icon name='image' color1='var(--color-default-foreground-300)' size='20' />
    {#if is_editing}
      {#if placeholder}
        <p class='text-preamble'>{placeholder}</p>
      {/if}
      {#if hint}
        <p class='text-caption'>{hint}</p>
      {/if}
    {/if}
  </div>
{/if}

<style lang='postcss'>
  /* Like a bare <img>, a <picture> takes its container's corners (its <img> inherits them in turn) */
  picture {
    border-radius: inherit;
  }
  img {
    display: block;
    border: none;
    border-radius: inherit;
    overflow: clip;
    height: auto;
    object-fit: cover;
    &.fit-to-frame {
      object-fit: contain;
    }
    &.checkerboard {
      --c: oklch(from var(--color-default-foreground) l c h / 0.1);
      --checker-size: 10px;
      background-image: linear-gradient(45deg, var(--c) 25%, transparent 25%, transparent 75%, var(--c) 75%, var(--c)),
                      linear-gradient(45deg, var(--c) 25%, transparent 25%, transparent 75%, var(--c) 75%, var(--c));
      background-position: 0 0, calc(var(--checker-size) / 2) calc(var(--checker-size) / 2);
      background-size: var(--checker-size) var(--checker-size);
    }
  }

  .video-wrapper {
    width: 100%;
    overflow: clip;
    border-radius: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    & > video {
      display: block;
      flex-shrink: 0;
      object-fit: unset;
    }
    &.cover-by-height > video {
      width: auto;
      height: 100%;
      max-width: none;
    }
    &.cover-by-width > video {
      width: 100%;
      height: auto;
      max-height: none;
    }
    &.fit-to-frame > video {
      max-width: 100%;
      max-height: 100%;
    }
  }

  :global(*:has(> .media-item, > .video-wrapper):has(> .media-item-controls)) {
    /* parent of .media-item has to be relative for absolute placement of PlayPauseButton */
    position: relative;
    /* container query for showing/hiding controls */
    container-type: inline-size;
    :global(.media-item-controls) {
      /* place play/pause button top right of media-item */
      position: absolute;
      z-index: 2;
      bottom: 0;
      left: 0;
      padding-block: max(calc(0.5 * (var(--card-radius,0px) + var(--icon-default-size,0px))), var(--size-2));
      padding-inline-start: max(calc(0.5 * (var(--card-radius,0px) + var(--icon-default-size,0px))), var(--size-2));
      justify-content: space-between;
      height: fit-content;
      transition: opacity 250ms ease, display 250ms ease;
      transition-behavior: allow-discrete;
      flex-direction: row;
      gap: var(--size-2);
      @starting-style {
        opacity: 0;
      }
      @media (hover: hover), (width < 768px) {
        display: none;
        opacity: 0;
      }
      @container (width < 420px){
        @media (hover: none) {
          display: none;
          opacity: 0;
        }
      }
      :global(.media-item-mute .button-label) {
        --button-background-color: oklch(0 0 0 / 0.3);
        --button-icon-color1: #FFF;
        backdrop-filter: blur(10px);
        opacity: 0.4;
        border-radius: 50%;
      }
      :global(.media-item-mute:hover .button-label) {
        opacity: 1 !important;
      }
    }
  }
  :global(*:has(> .media-item, > .video-wrapper):has(> .media-item-controls):hover .media-item-controls) {
    @media (hover: hover) {
      display: flex;
      opacity: 1;
    }
  }
  /* Why did we place this in the center for fit-to-frame images? */
  /* :global(*:has(> .media-item.fit-to-frame):has(> .media-item-controls)) {
    :global(.media-item-controls) {
      top: 50%;
      right: 50%;
      transform: translate(50%, -50%);
    }
  } */
  :global(*:has(> .media-item.fit-to-frame, > .video-wrapper.fit-to-frame):has(> .media-item-controls):hover) {
    :global(.media-item-controls) {
      opacity: 1;
    }
  }


  .placeholder-image {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--size-4);
    padding: var(--size-4) var(--size-4);
    background-color: var(--color-default-background-50);
    color: var(--color-default-foreground-300);
    /* prevent images from shrinking smaller than their size */
    flex-shrink: 0;
  }  
</style>
